import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Recaptcha from 'react-recaptcha';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { CmdAlert } from '@commander-services/gui-components';
import { useLocation, Link } from 'wouter';
import localforage from 'localforage';
import { useQueryClient } from '@tanstack/react-query';
import { LanguageContextProps, defaultLanguage } from '../LanguagesContext';
import LoginService from '../../Services/LoginService';
import JWTService from '../../Services/JWTService';
import { getDefaultCustomer, getLanguage, setLanguage } from '../../Services/UserService';
import { RESPONSE_OK } from '../../Services/ApiService';
import * as LoginState from './LoginState';
import Verify from '../Verify';
import SvgIconService from '../../Services/SvgIconService';
import { URL_LOGIN } from '../../router/constants';
import { hideMessage } from '../Toastr/ToastService';
import MaintenanceToast from '../Maintenance/Toast';
import { IToastr } from '../Toastr/ToastrState';
import * as ToastrState from '../Toastr/ToastrState';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import * as LanguageService from '../../Services/LanguageService';
import { gdprSettingsAtom, localeStringsAtom } from '../../store/recoil/AppState';
import { ITranslation } from '../../Services/LanguageService';
import { selectedCustomersAtom, selectedTempCustomersAtom } from '../../store/recoil/customers';
import { selectedVehiclesAtom } from '../../store/recoil/vehicles';
import useAnalytics from '../../hooks/useAnalytics';
import { isDemo, isNotDemo } from '../../Services/DemoService';
import { mapInfoWindowMapSettingsAtom } from '../OnlineMapNew/ToolbarLayer/VehicleChipsControl/InfoWindowState';

const logoSvg = SvgIconService.logo();

interface IComponentProps {
  context: LanguageContextProps;
}

let recaptchaInstance: Recaptcha | null;

const TWO_FA_SUSPENDED = '2fa_suspended';
const TWO_FA_REQUIRED = '2fa_required';

function Login(props: IComponentProps): JSX.Element {
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setUsername: setUsernameToAnalytics } = useAnalytics();
  const { formatMessage: f } = useIntl();
  useDocumentTitle(f({ id: 'loginForm.login' }));
  const { context } = props;
  const [_location, setLocation] = useLocation();
  const setLocaleStrings = useSetRecoilState<ITranslation>(localeStringsAtom);
  const setToken = useSetRecoilState<string | null>(LoginState.token);
  const setSelectedVehicles = useSetRecoilState<number[]>(selectedVehiclesAtom);
  const [toastr] = useRecoilState<IToastr | null>(ToastrState.toastr);
  const setGdprSettings = useSetRecoilState<Record<string, any>>(gdprSettingsAtom);

  const [username, setUsername] = React.useState<string | null>(isDemo() ? 'cccdemo' : null);
  const [password, setPassword] = React.useState<string | null>(isDemo() ? 'cccdemo' : null);
  const [autoLogoutMessage, setAutoLogoutMessage] = React.useState<string | null>(null);
  const [alert, setAlert] = React.useState<{
    title: string;
    message?: string;
    messagePayload?: Record<string, any> | null;
    type: string;
  } | null>(null);
  const [buttonLoader, setButtonLoader] = React.useState<boolean>(false);
  const [recaptchaToken, setRecaptchaToken] = React.useState<string | null>(null);
  const [isWithRecaptcha, setIsWithRecaptcha] = React.useState<boolean>(false);
  const [isTwoFactorVerification, setIsTwoFactorVerification] = React.useState<boolean>(false);
  const [verifyData, setVerifyData] = React.useState<{ id: number; phone: string } | null>(null);
  const setSelectedCustomers = useSetRecoilState<number[]>(selectedCustomersAtom);
  const setSelectTempCustomers = useSetRecoilState<number[]>(selectedTempCustomersAtom);
  const setMapSettingsValues = useSetRecoilState(mapInfoWindowMapSettingsAtom);

  const handleEnterKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      const submitButton = document.getElementById('submitLogin');
      if (submitButton) {
        submitButton.click();
      }
    }
  };

  React.useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const usernameParams = params.get('username');
    if (usernameParams) {
      setUsername(usernameParams);
    }
    const autologoutMessageInLocalStorage = localStorage.getItem('autologoutMessage');
    if (autologoutMessageInLocalStorage) {
      setAutoLogoutMessage(autologoutMessageInLocalStorage);
      localStorage.removeItem('autologoutMessage');
    }
    document.body.addEventListener('keyup', handleEnterKey);
    // invalid token
    if (
      toastr &&
      toastr.message &&
      toastr.shown &&
      (toastr.color === 'error' || toastr.color === 'warning')
    ) {
      trackEvent(['login', 'submit', 'invalid token']);
      setAlert({
        title: f({ id: 'toastr.warning' }),
        message: f({ id: toastr.message }, { ...toastr.messagePayload }),
        type: toastr.color,
      });
    }

    return () => {
      document.body.removeEventListener('keyup', handleEnterKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    trackEvent(['login', 'click', 'Submit form']);

    if (username && password && !alert && !(isWithRecaptcha && !recaptchaToken)) {
      setButtonLoader(true);

      if (toastr && toastr.shown) {
        hideMessage();
      }

      const response = await LoginService.signIn(
        username,
        password,
        defaultLanguage,
        recaptchaToken
      );
      if (recaptchaInstance) {
        recaptchaInstance.reset();
        setRecaptchaToken(null);
      }
      if (response && response.status === 429) {
        trackEvent(['login', 'submit', 'too many attempts']);
        setAlert({
          title: f({ id: 'toastr.error' }),
          message: f({ id: 'toast.auth.tooManyAttempts' }),
          type: 'error',
        });
        setButtonLoader(false);
      }
      if (response && response.data) {
        if (response.data.recaptcha) {
          setIsWithRecaptcha(true);
        } else {
          setIsWithRecaptcha(false);
        }
        if (response.data.status === RESPONSE_OK) {
          trackEvent(['login', 'submit', 'success']);
          LoginService.setPermissions(response.data.customers);
          setGdprSettings(response.data.gdprProfiles);
          LoginService.setUserData(response.data.user);
          setUsernameToAnalytics(response.data.user.username);
          if (response.data.user.language !== getLanguage() && isNotDemo()) {
            setLanguage(response.data.user.language);
            try {
              const data = await queryClient.fetchQuery({
                queryKey: ['translations', response.data.user.language],
                queryFn: () => LanguageService.getTranslation(response.data.user.language),
              });
              if (data) {
                setLocaleStrings(data);
              }
            } catch (error) {
              console.error('Error fetching translations', error);
            }
          }
          await localforage.setItem('gdprSettings', response.data.gdprProfiles);
          JWTService.storeSocketServerToken(response.data.socketServerToken);
          JWTService.storeToken(response.data.token);
          setToken(response.data.token);
          const defaultCustomers = getDefaultCustomer();
          setSelectedCustomers(defaultCustomers);
          setSelectTempCustomers(defaultCustomers);
          setSelectedVehicles([]);
          setMapSettingsValues(response.data.user.mapSettings);
          // setLanguage(getLanguage());

          if (context.updateValue) {
            const language = getLanguage();
            if (language) {
              context.updateValue('language', language);
            }
          }
          if (window.location.pathname === URL_LOGIN) {
            setLocation('/');
          }
          // if (gdprSettings) {
          //  // window.location.reload();
          // }
        } else if (response.data.status === TWO_FA_REQUIRED) {
          setIsTwoFactorVerification(true);
          setVerifyData({ id: response.data.id, phone: response.data.phone });
        } else if (response.data.status === TWO_FA_SUSPENDED) {
          trackEvent(['login', 'submit', '2fa_suspended']);
          setAlert({
            title: f({ id: 'toastr.error' }),
            message: f({ id: 'toast.auth.2fa.suspended' }),
            type: 'error',
          });
          setButtonLoader(false);
        } else {
          setAlert({
            title: f({ id: 'toastr.warning' }),
            message: f(
              { id: response.data.toast.message },
              { ...response.data.toast.messagePayload }
            ),
            type: 'error',
          });
          trackEvent(['login', 'submit', response.data.toast.message]);
          setButtonLoader(false);
        }
      }
    } else {
      if (username === null) {
        setUsername('');
      }
      if (password === null) {
        setPassword('');
      }
    }
  };

  const handleResend = () => {
    setIsTwoFactorVerification(false);
  };

  const handleName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
    if (alert) {
      setAlert(null);
    }
  };

  const handlePass = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    if (alert) {
      setAlert(null);
    }
  };

  const closeDeactivateWarning = () => {
    trackEvent(['login', 'click', 'Close deactivate warning']);
    setAutoLogoutMessage(null);
  };

  const verifyCallback = (verifyToken?: string) => {
    if (verifyToken) {
      if (username && password) {
        setAlert(null);
      }
      setRecaptchaToken(verifyToken);
    }
  };

  const expCallback = () => {
    setRecaptchaToken(null);
  };

  // const hideMaintenanceToast = () => {
  //   localStorage.setItem('hideMaintenanceToast', 'true');
  // };

  const closeAlert = () => {
    trackEvent(['login', 'click', 'Close alert']);
    setAlert(null);
  };

  return (
    <>
      {isTwoFactorVerification && (
        <Verify
          data={verifyData}
          context={context}
          stateProps={{ username, password, recaptchaToken }}
          resend={handleResend}
        />
      )}

      {!isTwoFactorVerification && (
        <div className="w-login">
          {autoLogoutMessage && (
            <CmdAlert
              id="login-auto-logout"
              data={{
                title: f({ id: 'login.autoLogout' }),
                message: autoLogoutMessage,
                type: 'warning',
              }}
              isAbsolute={true}
              onCloseCallback={closeDeactivateWarning}
            />
          )}
          <div className="w-login-content">
            <CmdAlert id="login-alert" data={alert} onCloseCallback={closeAlert} />
            {/* {!alert && <Toastr showAsAltert={true} onCloseCallback={hideMaintenanceToast} />} */}
            <MaintenanceToast showAsAlert={true} />
            <div className="b-login">
              <div className="headline">
                <img src={logoSvg} alt="logo" />
              </div>
              <form id="loginForm" onSubmit={handleSubmit}>
                <div className="p-8">
                  <div className={`form-group m-0 mb-2 ${username === '' ? 'has-error' : ''}`}>
                    <label className="font-weight-bold mb-1">
                      <FormattedMessage id="loginForm.username" />
                    </label>
                    <input
                      type="text"
                      id="loginUsername"
                      data-cy="loginUsername"
                      className="form-control"
                      value={username || ''}
                      onChange={handleName}
                      autoFocus={true}
                      readOnly={isDemo()}
                    />
                  </div>
                  <div
                    className={`form-group m-0 ${
                      password === '' || (alert && !isWithRecaptcha) ? 'has-error' : ''
                    }`}
                  >
                    <label className="font-weight-bold mb-1">
                      <FormattedMessage id="loginForm.password" />
                    </label>
                    <input
                      type="password"
                      id="loginPassword"
                      data-cy="loginPassword"
                      className="form-control"
                      autoComplete="off"
                      value={password || ''}
                      onChange={handlePass}
                      readOnly={isDemo()}
                    />
                  </div>
                  {/* <div style={{ textAlign: 'center', marginBottom: '-15px', marginTop: '12px' }}>
                    <OnlineHelp topic="login" />
                  </div> */}
                  {isWithRecaptcha && (
                    <div className="form-group m-0 mb-1 mt-3">
                      <br />
                      <Recaptcha
                        sitekey="6LccnMUZAAAAAPLbsAfx2RNJbFX8Fuhwxhpq1Ydb"
                        render="explicit"
                        size="normal"
                        hl={defaultLanguage}
                        verifyCallback={verifyCallback}
                        // eslint-disable-next-line no-return-assign
                        ref={(e: any) => (recaptchaInstance = e)}
                        expiredCallback={expCallback}
                      />
                    </div>
                  )}
                </div>

                <div className="foot py-6 px-8">
                  {buttonLoader ? (
                    <div className="e-button e-button--gray d-flex w-100">
                      <i className="cmd-icon-loader animation-spin" />
                    </div>
                  ) : (
                    <button
                      id="submitLogin"
                      data-cy="loginSubmit"
                      type="submit"
                      disabled={
                        !!(username === '' || password === '' || alert) ||
                        (isWithRecaptcha && !recaptchaToken)
                      }
                      className="e-button e-button--gray d-flex w-100"
                    >
                      {f({ id: 'loginForm.login' })}
                    </button>
                  )}
                  {isNotDemo() && (
                    <Link href="forgot-password">
                      <a href="#" id="forgottenPassword" className="fs-3 mt-1 d-inline-block">
                        {f({ id: 'loginForm.forgottenPassword' })}
                      </a>
                    </Link>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default React.memo(Login);
